import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Pagination,
  Skeleton,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import QrCode2Icon from "@mui/icons-material/QrCode2";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import ArrowOutwardRoundedIcon from "@mui/icons-material/ArrowOutwardRounded";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../../../auth/useAuth";
import {
  getErrorMessage,
  getRequest,
  MANUFACTURING_API,
  postRequest,
} from "./api";
import {
  EmptyStateCard,
  PageShell,
  SectionCard,
  SummaryCard,
  panelSx,
  statusChip,
} from "./ManufacturingCommon";

function OrderDetailSkeleton() {
  const summaryCards = Array.from({ length: 4 });
  const briefRows = Array.from({ length: 6 });
  const listRows = Array.from({ length: 5 });

  return (
    <PageShell title="Manufacturing Order" subtitle="Loading production order details">
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            sm: "1fr 1fr",
            md: "1fr 1fr 1fr 1fr",
          },
          gap: 2,
          mb: 3,
          width: "100%",
        }}
      >
        {summaryCards.map((_, index) => (
          <Box key={index} sx={{ ...panelSx, p: 2.2, minHeight: 150 }}>
            <Skeleton variant="text" width="46%" height={18} />
            <Skeleton variant="text" width="54%" height={34} sx={{ mt: 1 }} />
            <Skeleton variant="text" width="72%" height={22} sx={{ mt: 1 }} />
          </Box>
        ))}
      </Box>

      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
        <Box sx={{ display: "flex", flexDirection: { xs: "column", md: "row" }, gap: 2, width: "100%" }}>
          {[0, 1].map((panel) => (
            <Box key={panel} sx={{ width: { xs: "100%", lg: "50%" } }}>
              <Box sx={{ ...panelSx, p: 2.2 }}>
                <Skeleton variant="text" width="38%" height={30} />
                <Skeleton variant="text" width="74%" height={22} />
                <Stack spacing={1.2} sx={{ mt: 2 }}>
                  {(panel === 0 ? briefRows : listRows).map((_, index) => (
                    <Box
                      key={index}
                      sx={{
                        p: 1.4,
                        borderRadius: 3,
                        backgroundColor: "#fff",
                        border: "1px solid rgba(226,232,240,0.9)",
                      }}
                    >
                      <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2}>
                        <Box sx={{ flex: 1 }}>
                          <Skeleton variant="text" width="46%" height={22} />
                          <Skeleton variant="text" width="68%" height={20} />
                        </Box>
                        <Skeleton variant="rounded" width={92} height={26} sx={{ borderRadius: 999 }} />
                      </Stack>
                    </Box>
                  ))}
                </Stack>
              </Box>
            </Box>
          ))}
        </Box>

        <Box sx={{ width: "100%" }}>
          <Box sx={{ ...panelSx, p: 2.2 }}>
            <Skeleton variant="text" width="30%" height={30} />
            <Skeleton variant="text" width="66%" height={22} />
            <Stack spacing={1.4} sx={{ mt: 2 }}>
              {listRows.map((_, index) => (
                <Box key={index} sx={{ ...panelSx, p: 1.75, boxShadow: "none" }}>
                  <Skeleton variant="text" width="42%" height={24} />
                  <Skeleton variant="text" width="76%" height={20} />
                </Box>
              ))}
            </Stack>
          </Box>
        </Box>
      </Box>
    </PageShell>
  );
}

export default function ManufacturingOrderDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { activeOrg } = useAuth();
  const token = localStorage.getItem("authToken");
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [openAllocate, setOpenAllocate] = useState(false);
  const [quantity, setQuantity] = useState("");
  const [openCancelConfirm, setOpenCancelConfirm] = useState(false);
  const [loadingCancel, setLoadingCancel] = useState(false);
  const [serialPage, setSerialPage] = useState(1);
  const [unitPage, setUnitPage] = useState(1);

  const serialsPerPage = 5;
  const unitsPerPage = 6;

  const loadOrder = useCallback(async () => {
    if (!token || !activeOrg?.id || !id) return;
    setLoading(true);
    setError("");
    try {
      const data = await getRequest(
        `${MANUFACTURING_API}/orders/${id}?include_units=true&include_serials=true`,
        token,
        activeOrg,
      );
      setOrder(data);
    } catch (err) {
      setError(getErrorMessage(err, "Unable to load manufacturing order"));
    } finally {
      setLoading(false);
    }
  }, [token, activeOrg, id]);

  useEffect(() => {
    loadOrder();
  }, [loadOrder]);

  const handleAllocate = async () => {
    const parsed = Number(quantity);
    if (!Number.isFinite(parsed) || parsed <= 0) {
      setError("Please enter a valid serial allocation quantity");
      return;
    }
    try {
      await postRequest(
        `${MANUFACTURING_API}/orders/${id}/allocate-serials`,
        { quantity: parsed },
        token,
        activeOrg,
      );
      setSuccess("Serial numbers allocated");
      setOpenAllocate(false);
      setQuantity("");
      await loadOrder();
    } catch (err) {
      setError(getErrorMessage(err, "Unable to allocate serial numbers"));
    }
  };

  const handleRelease = async () => {
    try {
      await postRequest(
        `${MANUFACTURING_API}/orders/${id}/release`,
        {},
        token,
        activeOrg,
      );
      setSuccess("Order released");
      await loadOrder();
    } catch (err) {
      setError(getErrorMessage(err, "Unable to release order"));
    }
  };

  const handleCloseOrder = async () => {
    try {
      await postRequest(
        `${MANUFACTURING_API}/orders/${id}/close`,
        {},
        token,
        activeOrg,
      );
      setSuccess("Order closed");
      await loadOrder();
    } catch (err) {
      setError(getErrorMessage(err, "Unable to close order"));
    }
  };

  const handleCancelOrder = async () => {
    setLoadingCancel(true);
    try {
      await postRequest(
        `${MANUFACTURING_API}/orders/${id}/cancel`,
        {},
        token,
        activeOrg,
      );
      setSuccess("Order cancelled");
      await loadOrder();
    } catch (err) {
      setError(getErrorMessage(err, "Unable to cancel order"));
    } finally {
      setLoadingCancel(false);
      setOpenCancelConfirm(false);
    }
  };

  const serialAllocations = order?.serial_allocations || [];
  const manufacturingUnits = order?.units || [];

  const paginatedSerialAllocations = useMemo(() => {
    const startIndex = (serialPage - 1) * serialsPerPage;
    return serialAllocations.slice(startIndex, startIndex + serialsPerPage);
  }, [serialAllocations, serialPage]);

  const paginatedUnits = useMemo(() => {
    const startIndex = (unitPage - 1) * unitsPerPage;
    return manufacturingUnits.slice(startIndex, startIndex + unitsPerPage);
  }, [manufacturingUnits, unitPage]);

  useEffect(() => {
    const maxSerialPage = Math.max(
      1,
      Math.ceil(serialAllocations.length / serialsPerPage),
    );
    if (serialPage > maxSerialPage) {
      setSerialPage(maxSerialPage);
    }
  }, [serialAllocations.length, serialPage]);

  useEffect(() => {
    const maxUnitPage = Math.max(
      1,
      Math.ceil(manufacturingUnits.length / unitsPerPage),
    );
    if (unitPage > maxUnitPage) {
      setUnitPage(maxUnitPage);
    }
  }, [manufacturingUnits.length, unitPage]);

  if (loading) {
    return <OrderDetailSkeleton />;
  }

  if (!order) {
    return (
      <PageShell title="Manufacturing Order" subtitle="Order detail">
        <Alert severity="error">Order not found</Alert>
      </PageShell>
    );
  }

  const remaining = Math.max(
    0,
    Number(order.target_quantity || 0) -
    Number(order.summary?.allocated_serials || 0),
  );
  const orderMeta = [
    ["Partner", order.partner?.name || "Partner pending"],
    ["Product", order.product?.name || "Product pending"],
    ["PO Number", order.purchase_order_number || "Not set"],
    ["Customer Ref", order.customer_reference || "Not set"],
    ["Base OS", order.base_os_version || "Not set"],
    ["Test Plan", order.test_plan_version || "Not set"],
  ];

  return (
    <PageShell
      title={order.order_code}
      subtitle={`${order.partner?.name || "Partner pending"} | ${order.product?.name || "Product pending"} | target ${order.target_quantity}`}
      actions={
        <Stack spacing={1.25}>
          <Button
            variant="outlined"
            startIcon={<QrCode2Icon />}
            onClick={() => setOpenAllocate(true)}
            sx={{ color: "#F8FAFC", borderColor: "rgba(255,255,255,0.18)" }}
          >
            Generate Serials
          </Button>
          <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
            <Button
              variant="outlined"
              onClick={handleRelease}
              disabled={!["DRAFT", "APPROVED"].includes(order.status)}
              sx={{ color: "#F8FAFC", borderColor: "rgba(255,255,255,0.18)" }}
            >
              Release
            </Button>
            <Button
              variant="outlined"
              startIcon={<CloseRoundedIcon />}
              onClick={() => setOpenCancelConfirm(true)}
              color="error"
              disabled={Number(order.summary?.allocated_serials || 0) > 0}
              sx={{ color: "#F8FAFC", borderColor: "rgba(255,255,255,0.18)" }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleCloseOrder}
              disabled={
                ![
                  "COMPLETED",
                  "PARTIALLY_COMPLETED",
                  "RELEASED",
                  "IN_PRODUCTION",
                ].includes(order.status)
              }
              sx={{
                backgroundColor: "#F8FAFC",
                color: "#0F172A",
                "&:hover": { backgroundColor: "#E2E8F0" },
              }}
            >
              Close Order
            </Button>
          </Stack>
        </Stack>
      }
    >
      {error ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      ) : null}
      {success ? (
        <Alert severity="success" sx={{ mb: 2 }}>
          {success}
        </Alert>
      ) : null}

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",          // mobile
            sm: "1fr 1fr",      // tablet
            md: "1fr 1fr 1fr 1fr", // desktop (4 columns)
          },
          gap: 2,
          mb: 3,
          width: "100%",
        }}
      >
        <SummaryCard
          title="Status"
          value={statusChip(order.status)}
          subtitle={`Target ${order.target_quantity}`}
          eyebrow="Order State"
        />

        <SummaryCard
          title="Allocated Serials"
          value={order.summary?.allocated_serials || 0}
          subtitle={`${remaining} remaining`}
          eyebrow="Serial Control"
        />

        <SummaryCard
          title="Units Passed"
          value={order.summary?.units_passed || 0}
          subtitle="QC passed units"
          eyebrow="Quality"
        />

        <SummaryCard
          title="Converted Assets"
          value={order.summary?.units_converted || 0}
          subtitle="Moved into inventory"
          eyebrow="Inventory Conversion"
        />
      </Box>

      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
        <Box sx={{ display: "flex", flexDirection: { xs: "column", md: "row" }, gap: 2, width: "100%" }}>
          <Box sx={{ width: { xs: "100%", lg: "50%" } }}>
            <SectionCard title="Order Brief" subtitle="Commercial, engineering, and execution context for this production run.">
              <Stack spacing={1.2}>
                {orderMeta.map(([label, value]) => (
                  <Box
                    key={label}
                    sx={{
                      p: 1.35,
                      borderRadius: 3,
                      backgroundColor: "#fff",
                      border: "1px solid rgba(226,232,240,0.9)",
                    }}
                  >
                    <Typography sx={{ fontSize: 12, fontWeight: 700, color: "#64748B" }}>
                      {label}
                    </Typography>
                    <Typography sx={{ mt: 0.55, fontSize: 14, fontWeight: 800, color: "#0F172A" }}>
                      {value}
                    </Typography>
                  </Box>
                ))}

                {order.notes && (
                  <Typography variant="body2" color="text.secondary">
                    Notes: {order.notes}
                  </Typography>
                )}
              </Stack>
            </SectionCard>
          </Box>

          <Box sx={{ width: { xs: "100%", lg: "50%" } }}>
            <SectionCard
              title="Serial Allocations"
              subtitle="Reserved unit identities created from the manufacturing order."
            >
              {serialAllocations.length ? (
                <Stack spacing={1.1}>
                  {paginatedSerialAllocations.map((row) => (
                    <Box
                      key={row.id}
                      sx={{
                        p: 1.4,
                        borderRadius: 3,
                        backgroundColor: "#fff",
                        border: "1px solid rgba(226,232,240,0.9)",
                      }}
                    >
                      <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2}>
                        <Box>
                          <Typography sx={{ fontWeight: 800, color: "#0F172A" }}>
                            {row.serial_number}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Reserved{" "}
                            {row.reserved_at
                              ? new Date(row.reserved_at).toLocaleString()
                              : "-"}
                          </Typography>
                        </Box>
                        {statusChip(row.status)}
                      </Stack>
                    </Box>
                  ))}
                  {serialAllocations.length > serialsPerPage ? (
                    <Pagination
                      page={serialPage}
                      count={Math.ceil(serialAllocations.length / serialsPerPage)}
                      onChange={(_, page) => setSerialPage(page)}
                      siblingCount={0}
                      boundaryCount={1}
                      color="primary"
                      shape="rounded"
                      sx={{ alignSelf: "center", pt: 0.5 }}
                    />
                  ) : null}
                </Stack>
              ) : (
                <EmptyStateCard
                  title="No serial allocations yet"
                  description="Generate serial numbers to create unit identities for this manufacturing order."
                />
              )}
            </SectionCard>
          </Box>
        </Box>
        <Box sx={{ width: "100%" }}>
          <SectionCard
            title="Manufacturing Units"
            subtitle="Live units created from this order and their current quality status."
          >
            {manufacturingUnits.length ? (
              <Stack spacing={2}>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
                  {paginatedUnits.map((unit) => (
                    <Box
                      key={unit.id}
                      sx={{ width: { xs: "100%", md: "100%", xl: "100%" } }}
                    >
                      <Box
                        onClick={() => navigate(`/manufacturing/units/${unit.id}`)}
                        sx={{
                          ...panelSx,
                          p: 1.75,
                          cursor: "pointer",
                          boxShadow: "none",
                          transition: "transform 0.2s ease, box-shadow 0.2s ease",
                          "&:hover": {
                            transform: "translateY(-2px)",
                            boxShadow: "0 14px 28px rgba(15,23,42,0.08)",
                          },
                        }}
                      >
                        <Stack
                          direction="row"
                          justifyContent="space-between"
                          spacing={2}
                        >
                          <Box sx={{ minWidth: 0 }}>
                            <Typography
                              sx={{ fontWeight: 800, color: "#0F172A" }}
                            >
                              {unit.serial_number}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              Base OS {unit.base_os_version || "Not recorded"} | Rework {unit.rework_count || 0}
                            </Typography>
                          </Box>
                          <Stack spacing={1} alignItems="flex-end">
                            {statusChip(unit.status)}
                            {unit.qc_result ? statusChip(unit.qc_result) : null}
                          </Stack>
                        </Stack>
                        <Button
                          size="small"
                          endIcon={<ArrowOutwardRoundedIcon />}
                          sx={{
                            mt: 1.25,
                            px: 0,
                            textTransform: "none",
                            fontWeight: 800,
                          }}
                        >
                          Open unit
                        </Button>
                      </Box>
                    </Box>
                  ))}
                </Box>
                {manufacturingUnits.length > unitsPerPage ? (
                  <Pagination
                    page={unitPage}
                    count={Math.ceil(manufacturingUnits.length / unitsPerPage)}
                    onChange={(_, page) => setUnitPage(page)}
                    siblingCount={0}
                      boundaryCount={1}
                    color="primary"
                    shape="rounded"
                    sx={{ alignSelf: "center" }}
                  />
                ) : null}
              </Stack>
            ) : (
              <EmptyStateCard
                title="No manufacturing units yet"
                description="Allocate serial numbers to create manufacturing units for this order."
              />
            )}
          </SectionCard>
        </Box>
      </Box>

      <Dialog
        open={openAllocate}
        onClose={() => setOpenAllocate(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: panelSx }}
      >
        <DialogTitle
          sx={{
            background:
              "linear-gradient(135deg, rgba(15,23,42,0.98), rgba(30,64,175,0.92) 58%, rgba(13,148,136,0.86))",
            color: "#F8FAFC",
          }}
        >
          Generate Serial Numbers
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Alert severity="info">
              Target quantity is {order.target_quantity}. Currently allocated:{" "}
              {order.summary?.allocated_serials || 0}.
            </Alert>
            <TextField
              label="Quantity"
              type="number"
              value={quantity}
              onChange={(e) => setQuantity(e.target.value)}
              fullWidth
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setOpenAllocate(false)}>Cancel</Button>
          <Button
            variant="contained"
            startIcon={<CheckCircleOutlineIcon />}
            onClick={handleAllocate}
          >
            Generate
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={openCancelConfirm}
        onClose={() => setOpenCancelConfirm(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 700 }}>Cancel Order</DialogTitle>

        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            Cancel order <b>{order?.order_code}</b>? This is only allowed before
            unit allocation.
          </Typography>
        </DialogContent>

        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={() => setOpenCancelConfirm(false)}
            variant="outlined"
          >
            No
          </Button>

          <Button
            onClick={handleCancelOrder}
            variant="contained"
            color="error"
            disabled={loadingCancel}
          >
            {loadingCancel ? <CircularProgress size={28} /> : "Yes, Cancel"}
          </Button>
        </DialogActions>
      </Dialog>
    </PageShell>
  );
}
